import DxfParser from 'dxf-parser';

/**
 * WebGL-free DXF renderer.
 * Parses a DXF and rasterizes common floor-plan entities to a 2D canvas.
 * Supports: LINE, LWPOLYLINE/POLYLINE (with bulge arcs), CIRCLE, ARC, ELLIPSE,
 * SPLINE (approx), POINT, TEXT/MTEXT (basic), and INSERT (recursive block expand).
 * Advanced entities (hatches, complex splines, some text styles) render approximately.
 *
 * Returns { dataUrl, width, height } — a raster image + intrinsic size so the
 * viewer can overlay pins in normalized (0-1) coordinates.
 */

const identity = (p) => ({ x: p.x, y: p.y });

const compose = (outer, inner) => (p) => outer(inner(p));

function makeInsertTransform(insert) {
  const pos = insert.position || { x: 0, y: 0 };
  const sx = insert.xScale != null ? insert.xScale : 1;
  const sy = insert.yScale != null ? insert.yScale : 1;
  const rot = ((insert.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  return (p) => {
    const x = p.x * sx;
    const y = p.y * sy;
    return {
      x: x * cos - y * sin + pos.x,
      y: x * sin + y * cos + pos.y,
    };
  };
}

function bulgeToArc(p1, p2, bulge) {
  // Returns polyline points approximating an arc between p1 and p2 with given bulge.
  const pts = [];
  const theta = 4 * Math.atan(Math.abs(bulge));
  const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
  if (dist === 0 || theta === 0) return [p1, p2];
  const radius = dist / (2 * Math.sin(theta / 2));
  const angleToMid = Math.atan2(p2.y - p1.y, p2.x - p1.x);
  const sign = bulge > 0 ? 1 : -1;
  const midDist = radius * Math.cos(theta / 2) * sign;
  const midX = (p1.x + p2.x) / 2 - midDist * Math.sin(angleToMid);
  const midY = (p1.y + p2.y) / 2 + midDist * Math.cos(angleToMid);
  const startAngle = Math.atan2(p1.y - midY, p1.x - midX);
  const steps = Math.max(6, Math.ceil((theta / Math.PI) * 24));
  for (let i = 0; i <= steps; i++) {
    const a = startAngle + sign * theta * (i / steps);
    pts.push({ x: midX + radius * Math.cos(a), y: midY + radius * Math.sin(a) });
  }
  return pts;
}

function arcPoints(cx, cy, r, a0, a1, ccw = true) {
  const pts = [];
  let start = a0;
  let end = a1;
  if (ccw && end < start) end += Math.PI * 2;
  if (!ccw && end > start) end -= Math.PI * 2;
  const span = Math.abs(end - start);
  const steps = Math.max(8, Math.ceil((span / (Math.PI * 2)) * 64));
  for (let i = 0; i <= steps; i++) {
    const a = start + (end - start) * (i / steps);
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  return pts;
}

function collect(entities, blocks, transform, out, depth = 0) {
  if (!entities || depth > 8) return;
  for (const e of entities) {
    if (!e || !e.type) continue;
    try {
      switch (e.type) {
        case 'LINE': {
          const v = e.vertices || [e.start, e.end].filter(Boolean);
          if (v && v.length >= 2) {
            out.polys.push([transform(v[0]), transform(v[1])]);
          }
          break;
        }
        case 'LWPOLYLINE':
        case 'POLYLINE': {
          const verts = e.vertices || [];
          if (verts.length < 2) break;
          const line = [];
          for (let i = 0; i < verts.length; i++) {
            const cur = verts[i];
            const next = verts[i + 1];
            line.push(transform({ x: cur.x, y: cur.y }));
            if (cur.bulge && next) {
              const seg = bulgeToArc({ x: cur.x, y: cur.y }, { x: next.x, y: next.y }, cur.bulge);
              seg.forEach((sp) => line.push(transform(sp)));
            }
          }
          const closed = e.shape || e.closed;
          if (closed && verts.length > 2) line.push(transform({ x: verts[0].x, y: verts[0].y }));
          out.polys.push(line);
          break;
        }
        case 'CIRCLE': {
          const c = transform(e.center);
          out.polys.push(arcPoints(c.x, c.y, e.radius, 0, Math.PI * 2));
          break;
        }
        case 'ARC': {
          const c = transform(e.center);
          // dxf-parser returns angles in radians
          const a0 = e.startAngle != null ? e.startAngle : 0;
          const a1 = e.endAngle != null ? e.endAngle : Math.PI * 2;
          out.polys.push(arcPoints(c.x, c.y, e.radius, a0, a1, true));
          break;
        }
        case 'ELLIPSE': {
          const c = e.center || { x: 0, y: 0 };
          const maj = e.majorAxisEndPoint || { x: 1, y: 0 };
          const ratio = e.axisRatio != null ? e.axisRatio : 1;
          const rMaj = Math.hypot(maj.x, maj.y);
          const rMin = rMaj * ratio;
          const rot = Math.atan2(maj.y, maj.x);
          const a0 = e.startAngle != null ? e.startAngle : 0;
          const a1 = e.endAngle != null ? e.endAngle : Math.PI * 2;
          const steps = 64;
          const line = [];
          for (let i = 0; i <= steps; i++) {
            const a = a0 + (a1 - a0) * (i / steps);
            const x = rMaj * Math.cos(a);
            const y = rMin * Math.sin(a);
            line.push(transform({
              x: c.x + x * Math.cos(rot) - y * Math.sin(rot),
              y: c.y + x * Math.sin(rot) + y * Math.cos(rot),
            }));
          }
          out.polys.push(line);
          break;
        }
        case 'SPLINE': {
          const cp = e.fitPoints && e.fitPoints.length ? e.fitPoints : e.controlPoints;
          if (cp && cp.length >= 2) {
            out.polys.push(cp.map((p) => transform({ x: p.x, y: p.y })));
          }
          break;
        }
        case 'POINT': {
          const p = transform(e.position || { x: 0, y: 0 });
          out.points.push(p);
          break;
        }
        case 'TEXT':
        case 'MTEXT': {
          const p = transform(e.startPoint || e.position || { x: 0, y: 0 });
          const h = (e.textHeight || e.height || 2);
          const txt = (e.text || '').toString().replace(/\\[A-Za-z0-9.|]+;?/g, '').slice(0, 120);
          if (txt) out.texts.push({ x: p.x, y: p.y, h, text: txt });
          break;
        }
        case 'INSERT': {
          const block = blocks && blocks[e.name];
          if (block && block.entities) {
            const t = compose(transform, makeInsertTransform(e));
            collect(block.entities, blocks, t, out, depth + 1);
          }
          break;
        }
        default:
          break;
      }
    } catch (err) {
      // ignore individual entity errors
    }
  }
}

export function renderDxfToImage(dxfText, maxSize = 2200) {
  const parser = new DxfParser();
  const dxf = parser.parseSync(dxfText);
  if (!dxf) throw new Error('Could not parse DXF');

  const out = { polys: [], points: [], texts: [] };
  collect(dxf.entities || [], dxf.blocks || {}, identity, out);

  // Bounding box
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const consider = (p) => {
    if (!p || !isFinite(p.x) || !isFinite(p.y)) return;
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  };
  out.polys.forEach((poly) => poly.forEach(consider));
  out.points.forEach(consider);
  out.texts.forEach((t) => consider({ x: t.x, y: t.y }));

  if (!isFinite(minX) || !isFinite(maxX) || maxX <= minX || maxY <= minY) {
    throw new Error('DXF has no renderable geometry');
  }

  const pad = 0.03 * Math.max(maxX - minX, maxY - minY);
  minX -= pad; minY -= pad; maxX += pad; maxY += pad;

  const worldW = maxX - minX;
  const worldH = maxY - minY;
  const aspect = worldH / worldW;
  let width = maxSize;
  let height = Math.round(maxSize * aspect);
  if (height > maxSize) {
    height = maxSize;
    width = Math.round(maxSize / aspect);
  }
  width = Math.max(200, width);
  height = Math.max(200, height);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  const sx = width / worldW;
  const sy = height / worldH;
  const scale = Math.min(sx, sy);
  // center
  const offX = (width - worldW * scale) / 2;
  const offY = (height - worldH * scale) / 2;
  // world -> screen (flip Y)
  const W2S = (p) => ({
    x: offX + (p.x - minX) * scale,
    y: height - offY - (p.y - minY) * scale,
  });

  ctx.strokeStyle = '#1e293b';
  ctx.lineWidth = 1;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  out.polys.forEach((poly) => {
    if (!poly || poly.length < 2) return;
    ctx.beginPath();
    poly.forEach((wp, i) => {
      const s = W2S(wp);
      if (i === 0) ctx.moveTo(s.x, s.y);
      else ctx.lineTo(s.x, s.y);
    });
    ctx.stroke();
  });

  ctx.fillStyle = '#1e293b';
  out.points.forEach((wp) => {
    const s = W2S(wp);
    ctx.beginPath();
    ctx.arc(s.x, s.y, 1.5, 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.fillStyle = '#334155';
  out.texts.forEach((t) => {
    const s = W2S({ x: t.x, y: t.y });
    const fontPx = Math.max(8, Math.min(40, t.h * scale));
    ctx.font = `${fontPx}px sans-serif`;
    ctx.fillText(t.text, s.x, s.y);
  });

  return { dataUrl: canvas.toDataURL('image/png'), width, height };
}
